import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Gift, UserPlus, LogIn } from "lucide-react";

export default function AuthPage({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen bg-background flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Gift className="w-12 h-12 text-primary mx-auto" />
          <h1 className="font-anton text-4xl sm:text-5xl uppercase tracking-tight mt-5 text-white">
            {isRegister ? "Create Account" : "Welcome Back"}
          </h1>
          <p className="text-white/60 mt-3 text-sm">
            {isRegister
              ? "Join Blend Rewards — earn points on every order and track your history."
              : "Log in to see your points and order history."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <input
              data-testid="auth-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
            />
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
          />
          <input
            data-testid="auth-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isRegister ? "Choose a password (min 6 chars)" : "Password"}
            className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
          />

          {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

          {!isRegister && (
            <div className="text-right -mt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-white/50 hover:text-primary uppercase tracking-widest">
                Forgot password?
              </Link>
            </div>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
            {isRegister ? "Create account" : "Log in"}
          </button>
        </form>

        <p className="text-center text-sm text-white/50 mt-8">
          {isRegister ? "Already have an account?" : "New to Jersey Blend?"}{" "}
          <button
            data-testid="auth-toggle"
            onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
            className="text-primary hover:text-white font-bold uppercase tracking-widest"
          >
            {isRegister ? "Log in" : "Create an account"}
          </button>
        </p>
        <p className="text-center mt-4">
          <Link to="/" className="text-white/40 hover:text-white text-xs uppercase tracking-widest">← Back to store</Link>
        </p>
      </div>
    </div>
  );
}
